import { FormOutlined } from '@ant-design/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppDescription from '@nxsys/components/AppDescription';
import AppIconCard from '@nxsys/components/AppIconCard';
import AppIconCurrency from '@nxsys/components/AppIconCurrency';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { InvoiceTypeEnum } from '@nxsys/constants/AppEnums';
import {
  useGetInvoice,
  usePreviewInvoice,
  useUpdateCreditNote,
} from '@nxsys/hooks/api/invoice/useInvoiceApi';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { UpdateInvoiceByIdParams } from '@nxsys/types/api';
import { Icons } from '@nxsys/types/icons';
import { Rate } from '@nxsys/types/models/invoice';
import { showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Col, Descriptions, Divider, Row, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { toNumber } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import InvoiceForm, {
  InvoiceFormRef,
  InvoiceFormValues,
} from '../components/InvoiceForm';
import { vatTotalLabel } from './vat-total-label';

const { Text } = Typography;

const CreditNoteDetail = () => {
  const { messages } = useTypedIntl();
  const { payrollCountry, isBureau } = useSelector((state: AppState) => state.appStore);

  const params = useParams();
  const navigate = useNavigateWithQuery();
  const {
    run: getInvoiceRun,
    value: invoiceValue,
    loading: isGetInvoicePending,
  } = useGetInvoice();

  const {
    value: updatedCreditNoteValue,
    run: updateCreditNoteRun,
    loading: isUpdateCreditNotePending,
  } = useUpdateCreditNote();

  const creditNoteData = useMemo(
    () => updatedCreditNoteValue?.data ?? invoiceValue?.data,
    [updatedCreditNoteValue, invoiceValue],
  );

  const { run: previewInvoiceRun, loading: isPreviewInvoicePending } =
    usePreviewInvoice();

  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (params.id) {
      getInvoiceRun({ id: params.id });
    }
  }, [params.id]);

  const formRef = useRef<InvoiceFormRef>(null);

  const handleGoBack = () => navigate('/invoices/credit-notes');

  const toggleEditing = () => setIsEditing((v) => !v);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <div className="flex items-center gap-x-3">
          <AppPageTitle
            separator=""
            prefix={creditNoteData?.creditNo ?? ''}
            title=""
          />
        </div>
      </div>
    );
  }, [messages, creditNoteData]);

  const renderRightContent = useCallback(() => {
    if (!isEditing) {
      return (
        <div className="flex items-center gap-2">
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.preview']}
            onClick={handlePreview}
            loading={isPreviewInvoicePending}
            disabled={isPreviewInvoicePending}
          />
          <AppButton
            type="primary"
            name={messages['common.edit']}
            leftIcon={<FormOutlined />}
            onClick={toggleEditing}
            disabled={isGetInvoicePending}
          />
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          type="primary"
          name={messages['common.cancel']}
          onClick={toggleEditing}
        />
        <AppButton
          type="primary"
          name={messages['common.save']}
          onClick={() => formRef.current?.submit()}
          loading={isUpdateCreditNotePending}
          disabled={isUpdateCreditNotePending}
        />
      </div>
    );
  }, [
    messages,
    isEditing,
    isGetInvoicePending,
    isUpdateCreditNotePending,
    isPreviewInvoicePending,
    formRef,
  ]);

  const handleSubmit = async (values: InvoiceFormValues) => {
    if (!creditNoteData?.id) return;
    const params: UpdateInvoiceByIdParams = {
      id: creditNoteData.id,
      rates:
        values.rates?.map((rate) => ({
          ...rate,
          rateCode: (rate.rateCode as any)?.value ?? '',
          vatDetails: (rate.vatDetails as any)?.value ?? '',
          units: toNumber(rate.units),
          net: toNumber(rate.net),
          total: toNumber(rate.total),
          vatRate: rate.vatRate ?? '',
          includeInCisCalculation: true,
        })) ?? [],
    };
    try {
      const res = await updateCreditNoteRun(params);
      if (res?.success) {
        setIsEditing(false);
        await getInvoiceRun({ id: creditNoteData.id });
      } else {
        showMessage({
          type: 'error',
          content: messages['common.error'],
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const detailItems = useMemo(
    () => [
      {
        label: messages['common.agencyName'],
        value: creditNoteData?.agencyRef?.name ?? '',
      },
      {
        label: isBureau ? messages['common.bureau.companyName'] : messages['common.companyName'],
        value: creditNoteData?.companyRef?.companyName ?? '',
      },
      {
        label: messages['common.reference'],
        value: creditNoteData?.reference ?? '',
      },
      {
        label: messages['invoices.creditNo'],
        value: creditNoteData?.creditNo ?? '',
      },
      {
        label: messages['common.crnDate'],
        value: creditNoteData?.creditDate
          ? moment(creditNoteData?.creditDate).format('DD/MM/YYYY')
          : '',
      },
    ],
    [creditNoteData],
  );

  const rateColumns: ColumnsType<Rate> = [
    {
      key: 'rateCode',
      title: messages['assignment.rate.rateCode'],
      dataIndex: 'rateCode',
    },
    {
      key: 'description',
      title: messages['assignment.rate.description'],
      dataIndex: 'description',
    },
    {
      key: 'rate',
      title: messages['assignment.rate.rate'],
      dataIndex: 'rate',
      render: (rate: string) =>
        formatMoney(toNumber(rate), showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'unit',
      title: messages['common.unit'],
      dataIndex: 'units',
      width: 100
    },
    {
      key: 'net',
      title: messages['common.net'],
      dataIndex: 'net',
      render: (rate: string) =>
        formatMoney(toNumber(rate), showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'vatDetails',
      title: messages['assignment.rate.vatRate'],
      dataIndex: 'vatDetails',
      width: 200,
    },
    {
      key: 'total',
      title: messages['common.total'],
      dataIndex: 'total',
      render: (rate: string) =>
        formatMoney(toNumber(rate), showCurrencyByCountry(payrollCountry)),
    },
  ];

  const handlePreview = async () => {
    if (!creditNoteData?.id) return;
    try {
      const res = await previewInvoiceRun({ id: creditNoteData.id });
      if (res?.data) {
        window.open(res.data, '_blank');
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={isGetInvoicePending}
    >
      {!isEditing ? (
        <>
          <AppCard className="p-[16px]">
            <AppIconCard
              icon={<Icons.IconInvoice />}
              title={messages['common.invoice']}
            />
            <Divider className="mt-[12px] mb-[16px]" />
            <AppDescription
              column={1}
              labelStyle={{ width: 200 }}
              bordered={false}
              colon={false}
              size="middle"
            >
              {detailItems.map((it, idx) => (
                <Descriptions.Item key={idx} label={it.label}>
                  <Text>{it.value}</Text>
                </Descriptions.Item>
              ))}
            </AppDescription>
          </AppCard>
          <Row className="mt-4" gutter={[18, 18]}>
            <Col sm={24} md={18}>
              <AppCard>
                <div className="p-[16px] pb-[12px]">
                  <AppIconCard
                    icon={<AppIconCurrency />}
                    title={messages['common.rates']}
                  />
                </div>
                <Divider className="my-0" />
                <AppTableContainer
                  data={creditNoteData?.rates ?? []}
                  columns={rateColumns}
                  loading={false}
                  pagination={false}
                  page={0}
                  total={creditNoteData?.rates?.length ?? 0}
                  pageSize={0}
                  scroll={{ x: 1000, y: 700 }}
                />
              </AppCard>
            </Col>
            <Col sm={24} md={6} className="max-sm:w-full">
              <AppCard>
                <div className="space-y-3 p-4">
                  <div className="flex justify-between items-center">
                    <Typography.Text>Net</Typography.Text>
                    <Typography.Text>
                      {formatMoney(toNumber(creditNoteData?.totalNet), showCurrencyByCountry(payrollCountry))}
                    </Typography.Text>
                  </div>
                  <div className="flex justify-between items-center">
                    <Typography.Text>Total No VAT</Typography.Text>
                    <Typography.Text>
                      {formatMoney(0, showCurrencyByCountry(payrollCountry))}
                    </Typography.Text>
                  </div>
                  <div className="flex justify-between items-center">
                    <Typography.Text>
                      {vatTotalLabel(creditNoteData?.rates)}
                    </Typography.Text>
                    <Typography.Text>
                      {formatMoney(toNumber(creditNoteData?.totalVat), showCurrencyByCountry(payrollCountry))}
                    </Typography.Text>
                  </div>
                  <Divider />
                  <div className="flex justify-between items-center">
                    <Typography.Text>
                      {messages['common.total']}
                    </Typography.Text>
                    <Typography.Text>
                      {formatMoney(toNumber(creditNoteData?.totalPrices), showCurrencyByCountry(payrollCountry))}
                    </Typography.Text>
                  </div>
                </div>
              </AppCard>
            </Col>
          </Row>
        </>
      ) : (
        <InvoiceForm
          formRef={formRef}
          onSubmit={handleSubmit}
          defaultValues={{
            agencyRef: {
              label: creditNoteData?.agencyRef?.name ?? '',
              value: creditNoteData?.agencyRef?.id ?? '',
            },
            companyRef: {
              label: creditNoteData?.companyRef?.companyName ?? '',
              value: creditNoteData?.companyRef?.id ?? '',
            },
            creditDate: creditNoteData?.creditDate
              ? moment(creditNoteData.creditDate).toISOString()
              : null,
            reference: creditNoteData?.reference,
            rates:
              creditNoteData?.rates?.map((rate) => ({
                ...rate,
                rateCode: { label: rate.rateCode, value: rate.rateCode },
                vatDetails: { label: rate.vatDetails, value: rate.vatDetails },
                vatCode:
                  rate.vatCode ??
                  // vatDetails format: "<desc> (<VATCode>) - <VATRate>" —
                  // grab the parenthesised group right before " - ".
                  rate.vatDetails?.match(/\(([^()]+)\)\s*-/)?.[1] ??
                  '',
                net: rate.net.toString(),
                rate: rate.rate.toString(),
                total: rate.total.toString(),
                units: rate.units.toString(),
                vatRate: rate.vatRate,
              })) ?? [],
          }}
          isEdit={true}
          invoiceType={InvoiceTypeEnum.CREDIT_NOTE}
        />
      )}
    </AppsContainer>
  );
};

export default CreditNoteDetail;
